/* @flow */
import Maybe from 'folktale/maybe';
import { createSelector } from 'reselect';
import { Map } from 'immutable';
import {
    defaultTo,
    filter,
    find,
    flowRight,
    identity,
    includes,
    isEmpty,
    isEqual,
    negate,
    reject,
    some
} from 'lodash/fp';

import {
    categories,
    deviceCaps,
    deviceModels,
    deviceTypes,
    GROUP,
    hublessDeviceTypes,
    manufacturers,
    thermostatDeviceTypes,
    bulbTypes
} from '~/core/constants/devices';
import {
    getCapabilityNameFromProps,
    getIdFromProps,
    getParentFromProps,
    getProp,
    getStateAttribute,
    stateIdentity
} from '~/core/state/selectors';

import type { DeviceItemsType, StateType } from './types';

// Why not importing these from products/selectors?
// Because that creates a circular dependency whuch is baaaad.
// and means `getProductItemById` is imported as `undefined`
const getAllProducts = state => state.products;
const getProductItems = createSelector(getAllProducts, products => products.productItems);
const getProductItemById = createSelector(getProductItems, getIdFromProps, (productItems, id) =>
    productItems.find(prod => prod.get('id') === id)
);

// FIX-ME: for some $flow reason this needed... https://gist.github.com/sandfox/c71d86087ecfe8f6bd1a4d4bfcd257a4
// Pretty sure it's a flow or flow-typed bug...
const getId = item => item && item.id;
const getType = item => item && item.type;

const isHublessDevice = device => includes(device.type, hublessDeviceTypes);
const isDevice = device => includes(device.type, Object.values(deviceTypes));

export const getDevices = (state: { devices: StateType }): StateType => state.devices;

export const getDeviceItems = createSelector([getDevices], devices => devices.deviceItems || []);

export const getIsDevicesLoaded = createSelector([getDevices], ({ isLoaded }) => isLoaded);

export const getDeviceById = createSelector([getDeviceItems, getIdFromProps], (items, id) => find(['id', id], items));

export const getDeviceType = createSelector([getDeviceById], flowRight(defaultTo(GROUP), getType));

export const getIsZone = createSelector([getDeviceById], device => {
    if (!device) {
        return false;
    }
    const zone = getProp('zone')(device);
    const tui = getProp('tui')(device);
    return (device.id === zone && typeof tui !== 'undefined') || (typeof zone !== 'undefined' && device.id !== zone);
});

export const getDeviceCategory = createSelector([getIsZone], isZone => (isZone ? categories.ZONE : categories.DEVICE));

const createDeviceByTypeSelector = (type: string) => createSelector([getDeviceItems], find(['type', type]));

const createDevicesByTypeSelector = (type: string) => createSelector([getDeviceItems], filter(['type', type]));

export const getHub = createDeviceByTypeSelector(deviceTypes.HUB);
export const getHueBridge = createDeviceByTypeSelector(deviceTypes.HUE_BRIDGE);
export const getHueBridgeId = createSelector(getHueBridge, getId);
export const getBoilerModule = createDeviceByTypeSelector(deviceTypes.BOILER_MODULE);
export const getBoilerModuleId = createSelector([getBoilerModule], getId);
export const getBoilerModuleVersion = createSelector([getBoilerModule], getProp('version'));
export const getOfflineDevices = createSelector([getDeviceItems], reject(getProp('online')));
export const getLeakSensors = createDevicesByTypeSelector(deviceTypes.LEAK_SENSOR);
export const getCameras = createDevicesByTypeSelector(deviceTypes.HIVE_CAMERA);
export const getAllVisibleDevices = createSelector(
    [getDeviceItems],
    flowRight(filter(isDevice), reject(isHublessDevice))
);

export const getHubDevices = createSelector([getDeviceItems], reject(isHublessDevice));
export const getHublessDevices = createSelector([getDeviceItems], filter(isHublessDevice));

export const getHubModel = createSelector([getHub], getProp('model'));

export const getIsHubNano1 = createSelector([getHubModel], isEqual(deviceModels.NANO1));
export const getIsHubNano2 = createSelector([getHubModel], model =>
    includes(model, [deviceModels.NANO2, deviceModels.SENSE])
);
export const getSenseDevice = createDeviceByTypeSelector(deviceTypes.SENSE);
// SENSE IS BOTH HUB AND DEVICE (SENSORS) SO WE MAKE TWO CHECKS HERE
export const getIsHubSense = createSelector([getHub, getSenseDevice], (hub, senseDevice) => !!hub && !!senseDevice);

export const hasSLT1Thermostat = createSelector([getDeviceItems], some(['props.model', deviceModels.SLT1]));

// $FlowFixMe
export const getDeviceRemovingStatus = createSelector(getDevices, devices => devices.isRemoving);
export const getDeviceErrorStatus = createSelector(getDevices, devices => !!devices.hasError);
export const getDeviceSavingStatus = createSelector(getDevices, devices => devices.saving);
export const getDeviceRestartingStatus = createSelector(getDevices, devices => devices.isRestarting);
export const getDeviceRestartingErrorStatus = createSelector(getDevices, devices => devices.hasRestartingError);

const createDevicePropSelector = (name: string) => createSelector([getDeviceById], getProp(name));

export const getDeviceModel = createDevicePropSelector('model');
export const getDeviceIPAddress = createDevicePropSelector('ipAddress');
export const getDeviceMacAddress = createDevicePropSelector('macAddress');
export const getDeviceConnectionType = createDevicePropSelector('connection');
export const getDeviceFirmwareVersion = createDevicePropSelector('version');
export const getDevicePowerSource = createDevicePropSelector('power');
export const getDeviceBattery = createDevicePropSelector('battery');
export const getDeviceSignal = createDevicePropSelector('signal');
export const getDeviceLastSeen = createDevicePropSelector('uptime');
export const getDeviceOnlineStatus = createDevicePropSelector('online');
export const getDeviceZoneId = createDevicePropSelector('zone');
export const getDeviceStatus = createDevicePropSelector('status');
export const getDeviceManufacturer = createDevicePropSelector('manufacturer');
export const getDeviceSerialNumber = createDevicePropSelector('hardwareIdentifier');

const createDeviceStateSelector = (name: string) => createSelector([getDeviceById], getStateAttribute(name));

export const getDeviceName = createDeviceStateSelector('name');
export const getConnectedDevices = createDeviceStateSelector('connectedDevices');
export const getDeviceResolution = createDeviceStateSelector('resolution');
export const getDeviceNightVision = createDeviceStateSelector('nightVision');
export const getCameraAudio = createDeviceStateSelector('cameraAudio');
export const getDeviceInvertImage = createDeviceStateSelector('invertImage');

export const getDeviceCapabilities = createSelector(createDevicePropSelector('capabilities'), defaultTo([]));

const createIsDeviceByTypeSelector = (type: string) => createSelector([getDeviceType], isEqual(type));

export const getIsHueBridge = createIsDeviceByTypeSelector(deviceTypes.HUE_BRIDGE);
export const getIsHub = createIsDeviceByTypeSelector(deviceTypes.HUB);

const isThermostat = ({ type }) => includes(type, thermostatDeviceTypes);

export const getPairedThermostatIdFromBoilerModuleId = createSelector(
    [getDeviceZoneId, getDeviceItems],
    (zone: string, deviceItems: Array<DeviceItemsType>) =>
        flowRight(getId, find(isThermostat), filter(['props.zone', zone]))(deviceItems)
);

export const getPairedBoilerModuleIdFromThermostatId = createSelector(
    [getDeviceName, getDeviceItems],
    (thermoStatName: Maybe, deviceItems: Array<DeviceItemsType>) =>
        thermoStatName.chain(name => Maybe.fromNullable(flowRight(getId, find(['state.zoneName', name]))(deviceItems)))
);

export const createGetThermostatsForUpgrade = (deviceCapability: string) =>
    createSelector(
        [createDevicesByTypeSelector(deviceTypes.THERMOSTAT_UI)],
        filter(flowRight(includes(deviceCapability), defaultTo([]), getProp('capabilities')))
    );

export const getThermostatsForHive2Upgrade = createGetThermostatsForUpgrade(deviceCaps.HIVE2_UPGRADE);

export const getIfAnyUpgradeableThermostats = createSelector(getThermostatsForHive2Upgrade, negate(isEmpty));

export const getThermostatsForSLT3Upgrade = createGetThermostatsForUpgrade(deviceCaps.SLT3B_REPLACEMENT);

export const getSLT3sWithNoZone = createSelector(
    [createDevicesByTypeSelector(deviceTypes.THERMOSTAT_UI)],
    flowRight(defaultTo([]), filter(['props.model', 'SLT3']), filter(['props.zone', undefined]))
);

export const getShouldRequestDevices = createSelector(
    [getDevices],
    devices => !devices.isFetching || devices.deviceItems.length
);

const getParentDevice = createSelector([getDeviceItems, getProductItemById], (devices, product = Map()) =>
    flowRight(defaultTo({}), find(['id', product.get('parent')]))(devices)
);

export const getParentHasCapability = createSelector(
    // $FlowFixMe
    [(state, { id }) => getParentDevice(state, { id }), getCapabilityNameFromProps],
    (parent, capabilityName) => flowRight(includes(capabilityName), defaultTo([]), getProp('capabilities'))(parent)
);

export const getHubHasCapability = createSelector([getHub, (state, name) => name], (hub, name) =>
    flowRight(includes(name), defaultTo([]), getProp('capabilities'))(hub)
);

export const getDevicesWithParentCapability = createSelector(
    // $FlowFixMe
    [state => getDeviceItems(state), stateIdentity, getCapabilityNameFromProps],
    (devices, state, capabilityName) =>
        filter(({ id }) => getParentHasCapability(state, { id, capabilityName }), devices)
);

export const getIsHueDevice = createSelector([getDeviceManufacturer], isEqual(manufacturers.PHILIPS));

export const getIsHueBulb = createSelector(
    [getIsHueDevice, getIsHueBridge],
    (isHueDevice, isHueBridge) => isHueDevice && !isHueBridge
);

export const getDevicesByParentId = createSelector([getDeviceItems, getParentFromProps], (devices, parentId) =>
    flowRight(filter(['parent', parentId]), reject(['id', parentId]))(devices)
);

export const getParentPairedThermostatIdFromBoilerModuleId = createSelector(
    [getParentDevice, stateIdentity],
    (parentDevice, state) => {
        return getPairedThermostatIdFromBoilerModuleId(state, { id: parentDevice.id, controlType: '' });
    }
);

export const getBulbItems = createSelector(getDeviceItems, filter(({ type }) => bulbTypes.includes(type)));

export const hasBulbs = createSelector(getBulbItems, some(identity));

export const getWhetherDeviceHasCapability = createSelector(
    [getDeviceById, getCapabilityNameFromProps],
    (device, capabilityName) => flowRight(includes(capabilityName), defaultTo([]), getProp('capabilities'))(device)
);

export const getBulbItemsCount = createSelector(getBulbItems, devices => devices.length);



// WEBPACK FOOTER //
// ./src/core/state/devices/selectors.js